import { onUnmounted } from 'vue'
import { onSnapshot } from 'firebase/firestore'

/**
 * Manages Firestore onSnapshot lifecycle.
 *
 * Automatically unsubscribes from all active listeners when the component unmounts.
 * Replaces the manual unsubscribe patterns scattered across 10+ views.
 *
 * @example
 * const { subscribe } = useFirestoreSubscription()
 *
 * // Single document
 * subscribe(doc(db, 'Users', uid), (snap) => {
 *   user.value = snap.data()
 * })
 *
 * // Collection / query
 * subscribe(query(collection(db, 'Orders'), where('status', '==', 'open')), (snap) => {
 *   orders.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
 * })
 *
 * @returns {{ subscribe: Function, track: Function, unsubscribeAll: Function }}
 */
export function useFirestoreSubscription() {
  const unsubscribers = []

  /**
   * Start a Firestore snapshot listener.
   *
   * @param {import('firebase/firestore').DocumentReference | import('firebase/firestore').Query} refOrQuery
   * @param {Function} callback - Called with (snapshot) on each change
   * @param {Function} [onError] - Optional error handler
   * @returns {Function} Unsubscribe function for this specific listener
   */
  function subscribe(refOrQuery, callback, onError) {
    const errorHandler = onError || ((err) => console.error('Firestore subscription error:', err))
    const unsub = onSnapshot(refOrQuery, callback, errorHandler)
    unsubscribers.push(unsub)
    return unsub
  }

  /**
   * Track an externally-created unsubscribe function (e.g. from a service wrapper).
   *
   * @param {Function} unsub - Unsubscribe function returned by a service helper
   * @returns {Function} The same unsubscribe function (for chaining / reassignment)
   */
  function track(unsub) {
    if (typeof unsub === 'function') {
      unsubscribers.push(unsub)
    }
    return unsub
  }

  /**
   * Unsubscribe from all active listeners.
   */
  function unsubscribeAll() {
    unsubscribers.forEach((unsub) => unsub())
    unsubscribers.length = 0
  }

  onUnmounted(unsubscribeAll)

  return { subscribe, track, unsubscribeAll }
}
